import React from "react";
import { NumberInput } from "../../ui/number-input";
import {
  DEPARTMENT_LABELS,
  LABOR_OVERRIDE_CLEAR_HINT,
  LABOR_OVERRIDE_LEAD,
  ZERO_LABOR_CONFIRMED_NOTE,
  hasNegativeLaborOverride,
  hasZeroLaborOverride,
} from "./traceLabels";
import LaborOverrideAlert from "./LaborOverrideAlert";

/**
 * Per-line labor override.
 *
 * These hours affect ONLY this estimate. Editing the shared rate card (which
 * would re-price every job in the category) is deliberately not here — that
 * lives in Rate Cards, behind admin, with its own review.
 *
 * The reducer marks a line `labor_source: "manual"` the moment any hour field
 * is edited. "Clear override" is a dedicated reducer action, not four zeroes:
 * a typed zero is a real override and would price the line with no shop labor.
 */

const OVERRIDE_FIELDS = [
  { field: "cnc_hours", label: DEPARTMENT_LABELS.cnc },
  { field: "cut_hours", label: DEPARTMENT_LABELS.cut },
  { field: "fab_hours", label: DEPARTMENT_LABELS.fab },
  { field: "finish_hours", label: DEPARTMENT_LABELS.paint },
];

function LaborOverridePanel({ lineItem, lineIndex, dispatch, panelRef }) {
  const isOverride =
    String(lineItem?.labor_source || "").toLowerCase() === "manual";

  const update = (field, value) => {
    dispatch({
      type: "UPDATE_LINE_ITEM_FIELD",
      index: lineIndex,
      field,
      value,
    });
  };

  const clearOverride = () => {
    dispatch({ type: "CLEAR_LINE_ITEM_LABOR_OVERRIDE", index: lineIndex });
  };

  // A line priced with no shop labor at all is one keystroke away from a typo,
  // so it shouts until someone says they meant it — and then stops shouting.
  const zeroLabor = hasZeroLaborOverride(lineItem);
  const zeroLaborConfirmed = Boolean(lineItem?.zero_labor_confirmed);

  const hasNegativeLabor = hasNegativeLaborOverride(lineItem);

  // While the loud warning is up it carries its own way back to the card —
  // two identical buttons on one panel is noise.
  const showClearOverride =
    isOverride && !hasNegativeLabor && !(zeroLabor && !zeroLaborConfirmed);

  return (
    <div className="est-override" ref={panelRef} tabIndex={-1}>
      <div className="est-override__hd">
        {/* The rules of this panel are on its title; what the fields do when
            blank is on the fields themselves, as a placeholder. Only the
            override state — a thing that has happened — stays on the page. */}
        <span
          className="est-override__title"
          title="Hours typed here price this line only and are for the whole line, not per piece. Leave every field blank to price from the rate card."
        >
          Hours for the whole line
        </span>
        {isOverride ? (
          <span className="est-override__badge">Override</span>
        ) : null}
      </div>

      {isOverride ? <p className="wb-hint">{LABOR_OVERRIDE_LEAD}</p> : null}

      <div className="est-fields est-fields--tight">
        {OVERRIDE_FIELDS.map(({ field, label }) => (
          <div key={field}>
            <label htmlFor={`labor-${lineIndex}-${field}`}>{label}</label>
            <NumberInput
              id={`labor-${lineIndex}-${field}`}
              align="right"
              step="0.25"
              min={0}
              value={
                // A non-overridden line often carries literal 0s in these
                // fields; showing them contradicts "leave blank to price from
                // the rate card". Only a manual line renders its numbers.
                lineItem?.labor_source === "manual"
                  ? (lineItem?.[field] ?? "")
                  : ""
              }
              onValueChange={(value) => update(field, value)}
              placeholder={isOverride ? undefined : "rate card"}
              aria-label={`${label} hours for line item ${lineIndex + 1}`}
              className="h-9 text-[13px]"
            />
          </div>
        ))}
      </div>

      <LaborOverrideAlert
        item={lineItem}
        lineIndex={lineIndex}
        dispatch={dispatch}
      />

      {zeroLabor && zeroLaborConfirmed && !hasNegativeLabor ? (
        <p className="wb-hint">{ZERO_LABOR_CONFIRMED_NOTE}</p>
      ) : null}

      {/* The foot used to caption the fields as well; that sentence now rides
          on the panel title. With nothing but the button left, an empty foot
          would just be dead vertical space. */}
      {showClearOverride ? (
        <div className="est-override__foot">
          <button
            type="button"
            className="wb-tbtn wb-tbtn--edge"
            onClick={clearOverride}
            title={LABOR_OVERRIDE_CLEAR_HINT}
          >
            Clear override
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default LaborOverridePanel;
